import get from 'lodash.get';
import pick from 'lodash.pick';
import sumBy from 'lodash.sumby';

import { AppState } from 'services/types';
import { Entity, EntityEntityTypeId } from 'services/entities/types';
import {
    VirtualizedTableColumns,
    VirtualizedTableColumnKeys,
    VirtualizedTableItem
} from 'services/virtualizedTable/types';
import { makeSelectTableColumnWidths } from 'services/columnWidths/selectors';
// @ts-ignore
import { selectCurrentSeasonIsComplete } from 'services/currentSeasonId/selectors';
import { selectEntitiesArray } from 'services/entities/selectors';
// @ts-ignore
import { selectCurrentEntityId } from 'services/currentEntityId/selectors';
import {
    makeSelectEntityTypeValue,
    makeSelectIsFirstDepth,
    makeSelectIsLastDepth,
    makeSelectPreviousEntityTypeValue,
    makeSelectIsCostCentreAllowed
    // @ts-ignore
} from 'services/entityTypes/selectors';
import { makeSelectHarvestSchemaValue } from 'services/schemas/selectors';
import { selectVarietyOptions } from 'services/varieties/selectors';
// @ts-ignore
import { makeSelectOptions as makeSelectEntityOptions } from 'components/EntityOptions/selectors';
import {
    FIELD_TYPE_DROPDOWN,
    FIELD_TYPE_DROPDOWN_MULTIPLE,
    FIELD_TYPE_STRING,
    FIELD_TYPE_NUMBER,
    FIELD_TYPE_DATE,
    FIELD_TYPE_LIST,
    FIELD_TYPE_CONFIRMATION_CHECKBOX
} from 'components/VirtualizedTable/constants';
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
// @ts-ignore
import { selectUsers } from 'services/people/selectors';
import { Person } from 'services/people/types';
import { selectIsAdmin } from 'services/usersMe/selectors';
import {
    selectIsHarvestEnabled,
    selectIsRemovalsEnabled
} from 'services/organizationConfig/selectors';
import formatDropdownOptions from 'utils/format-dropdown-options';

import { OwnProps } from './index';
import './styles.scss';

const selectUserOptions = createDeepEqualSelector(
    selectUsers,
    (users: Record<Person['email'], Person>) =>
        formatDropdownOptions(users, {
            keyField: 'email',
            textField: 'fullName'
        })
);

const selectEntityTypeId = (
    _state: AppState,
    props: OwnProps
): EntityEntityTypeId | undefined => props.entityTypeId;

const selectEntityTypeIdExistsInHarvestSchema = createDeepEqualSelector(
    [makeSelectHarvestSchemaValue('entityTypeId'), selectEntityTypeId],
    (harvestSchemaEntityTypeIdState, entityTypeId) =>
        harvestSchemaEntityTypeIdState === entityTypeId
);

const selectParentEntityOptions = createDeepEqualSelector(
    [
        (state: AppState): AppState => state,
        makeSelectPreviousEntityTypeValue('id')
    ],
    (state, previousEntityTypeId) =>
        makeSelectEntityOptions()(state, { entityTypeId: previousEntityTypeId })
);

const selectEntitiesTableColumns = createDeepEqualSelector(
    [
        makeSelectTableColumnWidths(),
        selectCurrentSeasonIsComplete,
        makeSelectIsFirstDepth(),
        makeSelectIsLastDepth(),
        makeSelectEntityTypeValue('isOrchard'),
        makeSelectIsCostCentreAllowed(),
        selectEntityTypeIdExistsInHarvestSchema,
        selectVarietyOptions,
        selectParentEntityOptions,
        selectUserOptions,
        selectIsHarvestEnabled,
        selectIsRemovalsEnabled,
        makeSelectEntityTypeValue('fieldLabel0'),
        makeSelectEntityTypeValue('fieldLabel1'),
        makeSelectEntityTypeValue('fieldLabel2'),
        makeSelectEntityTypeValue('fieldLabel3'),
        makeSelectEntityTypeValue('fieldLabel4'),
        makeSelectEntityTypeValue('customNameLabel'),
        makeSelectPreviousEntityTypeValue('name')
    ],
    (
        tableColumnWidths,
        currentSeasonIsCompleteState,
        isFirstDepth,
        isLastDepth,
        entityTypeIsOrchard: boolean,
        isCostCentreAllowed,
        entityTypeIdExistsInHarvestSchema,
        varietyOptions,
        parentEntityOptions: any,
        userOptions,
        isHarvestEnabled: boolean,
        isRemovalsEnabled: boolean,
        entityTypeFieldLabel0: string | null,
        entityTypeFieldLabel1: string | null,
        entityTypeFieldLabel2: string | null,
        entityTypeFieldLabel3: string | null,
        entityTypeFieldLabel4: string | null,
        entityTypeCustomNameLabel: string | null,
        previousEntityTypeName?: string
    ): VirtualizedTableColumns => {
        const disabled = currentSeasonIsCompleteState;

        const result: VirtualizedTableColumns = [];

        if (!isFirstDepth) {
            result.push({
                label: previousEntityTypeName,
                value: 'parentEntityId',
                width: 232,
                selectable: !disabled,
                type: FIELD_TYPE_DROPDOWN,
                options: parentEntityOptions,
                validators: 'isRequired'
            });
        }

        result.push({
            label: entityTypeCustomNameLabel || 'Name',
            value: 'name',
            width: 232,
            selectable: !disabled,
            type: FIELD_TYPE_STRING,
            validators: ['isRequired', 'isTrimmed']
        });

        if (entityTypeFieldLabel0) {
            result.push({
                label: entityTypeFieldLabel0,
                value: 'fieldValue0',
                width: 232,
                selectable: !disabled,
                type: FIELD_TYPE_STRING,
                emptyValueToNull: true
            });
        }

        if (entityTypeFieldLabel1) {
            const isNumeric = entityTypeFieldLabel1 === '# Rows';
            result.push({
                label: entityTypeFieldLabel1,
                value: 'fieldValue1',
                width: 232,
                selectable: !disabled,
                type: FIELD_TYPE_STRING,
                emptyValueToNull: true,
                validators: isNumeric ? ['isInteger', 'minValue0'] : []
            });
        }

        if (entityTypeFieldLabel2) {
            result.push({
                label: entityTypeFieldLabel2,
                value: 'fieldValue2',
                width: 232,
                selectable: !disabled,
                type: FIELD_TYPE_STRING,
                emptyValueToNull: true
            });
        }

        if (entityTypeFieldLabel3) {
            result.push({
                label: entityTypeFieldLabel3,
                value: 'fieldValue3',
                width: 232,
                selectable: !disabled,
                type: FIELD_TYPE_STRING,
                emptyValueToNull: true
            });
        }

        if (entityTypeFieldLabel4) {
            result.push({
                label: entityTypeFieldLabel4,
                value: 'fieldValue4',
                width: 232,
                selectable: !disabled,
                type: FIELD_TYPE_STRING,
                emptyValueToNull: true
            });
        }

        if (isFirstDepth && isHarvestEnabled) {
            result.push(
                {
                    label: 'Default QA',
                    value: 'inspectionUserEmails',
                    width: 232,
                    selectable: false,
                    type: FIELD_TYPE_DROPDOWN_MULTIPLE,
                    options: userOptions
                },
                {
                    label: 'Default QC',
                    value: 'timesheetUserEmails',
                    width: 232,
                    selectable: false,
                    type: FIELD_TYPE_DROPDOWN_MULTIPLE,
                    options: userOptions
                }
            );
        }

        if (isCostCentreAllowed) {
            result.push({
                formHeader: 'Payroll Integration',
                label: 'Cost Centre',
                value: 'costCentre',
                width: 152,
                selectable: !disabled,
                type: FIELD_TYPE_STRING,
                placeholder: `Leaving this blank will use the parent area's cost centre if that exists`,
                emptyValueToNull: true
            });
        }

        if (entityTypeIsOrchard) {
            result.push({
                label: 'Orchard RPIN',
                value: 'orchardRpin',
                width: 152,
                selectable: !disabled,
                type: FIELD_TYPE_STRING,
                validators: 'isOrchardRpin',
                emptyValueToNull: true
            });
        }

        if (entityTypeIdExistsInHarvestSchema && isHarvestEnabled) {
            result.push(
                {
                    formHeader: 'SAP Integration',
                    label: 'Management Area',
                    value: 'managementArea',
                    width: 232,
                    selectable: !disabled,
                    type: FIELD_TYPE_STRING,
                    validators: ['isRequired', 'isTrimmed']
                },
                {
                    label: 'Production Site',
                    value: 'productionSite',
                    width: 232,
                    selectable: !disabled,
                    type: FIELD_TYPE_STRING,
                    validators: ['isRequired', 'isTrimmed']
                }
            );
        }

        if (isLastDepth) {
            result.push(
                {
                    formColumnWidth: 16,
                    label: 'Row Spacing (m)',
                    value: 'rowSpacing',
                    width: 152,
                    type: FIELD_TYPE_NUMBER,
                    selectable: !disabled,
                    validators: [
                        'isNumber',
                        'maxValue99',
                        'isPositiveNumber',
                        'hasNoMoreThanTwoDecimalPlaces'
                    ],
                    emptyValueToNull: true
                },
                {
                    formHeader: 'Tree information',
                    formColumnWidth: 16,
                    label: 'Planting Date',
                    value: 'plantingDate',
                    width: 152,
                    type: FIELD_TYPE_DATE,
                    selectable: !disabled,
                    validators: 'isRequired',
                    initialValue: '2000-01-01'
                },
                {
                    formColumnWidth: 8,
                    label: 'Total Area (Ha)',
                    value: 'totalArea',
                    width: 152,
                    type: FIELD_TYPE_NUMBER,
                    selectable: !disabled,
                    validators: [
                        'isRequiredNumber',
                        'isNumber',
                        'maxValue1000000',
                        'minValue0'
                    ],
                    hasFooterSummary: true,
                    unit: 'Ha',
                    initialValue: 0
                },
                {
                    formColumnWidth: 8,
                    label: 'Number of Trees',
                    value: 'numberOfTrees',
                    width: 152,
                    type: FIELD_TYPE_NUMBER,
                    selectable: !disabled,
                    validators: [
                        'isRequiredNumber',
                        'isInteger',
                        'maxValue1000000',
                        'minValue0'
                    ],
                    hasFooterSummary: true,
                    unit: 'Trees',
                    initialValue: 0
                },
                {
                    formColumnWidth: 8,
                    label: 'Non-Productive',
                    value: 'isNonProductive',
                    width: 150,
                    type: FIELD_TYPE_CONFIRMATION_CHECKBOX,
                    selectable: false,
                    hasFooterSummary: false,
                    initialValue: false
                }
            );

            if (isRemovalsEnabled) {
                result.push(
                    {
                        label: 'Removals',
                        value: 'removals',
                        width: 150,
                        type: FIELD_TYPE_LIST,
                        formClassName: 'removals',
                        initialValue: [],
                        listColumns: [
                            {
                                label: 'Date',
                                value: 'date',
                                type: FIELD_TYPE_DATE,
                                validators: 'isRequired'
                            },
                            {
                                label: 'Total Area',
                                value: 'totalArea',
                                type: FIELD_TYPE_NUMBER,
                                validators: [
                                    'isRequired',
                                    'isNumber',
                                    'isPositiveNumber',
                                    'maxValue1000000'
                                ]
                            },
                            {
                                label: 'Number Of Trees',
                                value: 'numberOfTrees',
                                type: FIELD_TYPE_NUMBER,
                                validators: [
                                    'isRequired',
                                    'isInteger',
                                    'isPositiveNumber',
                                    'maxValue1000000'
                                ]
                            }
                        ]
                    },
                    {
                        label: 'Area Removed (Ha)',
                        value: 'removalsTotalArea',
                        width: 170,
                        type: FIELD_TYPE_NUMBER,
                        selectable: false,
                        hasFooterSummary: true,
                        unit: 'Ha',
                        initialValue: 0
                    },
                    {
                        label: 'Trees Removed',
                        value: 'removalsTotalTrees',
                        width: 150,
                        type: FIELD_TYPE_NUMBER,
                        selectable: false,
                        hasFooterSummary: true,
                        unit: 'Trees',
                        initialValue: 0
                    }
                );
            }
        }

        // TODO: display variety on the lowest depth
        // if (entityTypeIdExistsInHarvestSchema || !entityTypeIdExistsInHarvestSchema && isLastDepth) {
        if (entityTypeIdExistsInHarvestSchema) {
            result.push({
                label: 'Variety',
                value: 'defaultVarietyId',
                width: 232,
                selectable: !disabled,
                type: FIELD_TYPE_DROPDOWN,
                options: varietyOptions,
                validators: entityTypeIdExistsInHarvestSchema
                    ? 'isRequired'
                    : undefined
            });
        }

        return result.map((column, index) => {
            const { width, value } = column;
            return {
                ...column,
                width: get(tableColumnWidths, value, width),
                orderIndex: index
            };
        });
    }
);

const makeSelectEntitiesTableColumns = (): typeof selectEntitiesTableColumns =>
    selectEntitiesTableColumns;

const selectColumnKeys = createDeepEqualSelector(
    selectEntitiesTableColumns,
    (entitiesTableColumns) => {
        const columnKeys: VirtualizedTableColumnKeys = [];

        entitiesTableColumns.forEach((column) => {
            const { value } = column;
            columnKeys.push(value);
        });

        return columnKeys;
    }
);

const selectCurrentEntityEntitiesArray = createDeepEqualSelector(
    [selectEntitiesArray, selectCurrentEntityId],
    (entitiesArray, currentEntityIdState) =>
        entitiesArray.filter((entity) => {
            if (!currentEntityIdState) {
                return true;
            }

            if (!entity.parentEntityId) {
                return entity.id === currentEntityIdState;
            }

            const rootParentId =
                entity.path && entity.path.split('/').map(Number)[1];
            return rootParentId === currentEntityIdState;
        })
);

const selectRelevantEntitiesArray = createDeepEqualSelector(
    [selectCurrentEntityEntitiesArray, selectEntityTypeId],
    (currentEntityEntitiesArray, entityTypeIdState) => {
        if (!entityTypeIdState) {
            return currentEntityEntitiesArray;
        }
        return currentEntityEntitiesArray.filter(
            (value) =>
                value.entityTypeId === entityTypeIdState &&
                !value.archivedAt &&
                !value.deletedAt
        );
    }
);

const selectEntitiesArrayItems = createDeepEqualSelector(
    [selectRelevantEntitiesArray, selectColumnKeys],
    (relevantEntitiesArray, columnKeys) =>
        relevantEntitiesArray.map((entity: Entity): VirtualizedTableItem => {
            const item = pick(entity, columnKeys);

            const { removals } = item;

            if (removals && removals.length) {
                const totalArea = sumBy(
                    removals,
                    (removal) => removal.totalArea || 0
                );
                const totalTrees = sumBy(
                    removals,
                    (removal) => removal.numberOfTrees || 0
                );

                return {
                    ...item,
                    key: entity.uuid,
                    removalsTotalArea: totalArea,
                    removalsTotalTrees: totalTrees
                };
            }

            return {
                ...item,
                key: entity.uuid
            };
        })
);

const makeSelectEntitiesArrayItems = (): typeof selectEntitiesArrayItems =>
    selectEntitiesArrayItems;

const selectIsWritable = createDeepEqualSelector(
    selectIsAdmin,
    selectCurrentSeasonIsComplete,
    (isAdmin: boolean, currentSeasonIsComplete: boolean) =>
        isAdmin && !currentSeasonIsComplete
);

export {
    selectEntityTypeIdExistsInHarvestSchema,
    selectEntitiesTableColumns,
    makeSelectEntitiesTableColumns,
    selectEntitiesArrayItems,
    makeSelectEntitiesArrayItems,
    selectIsWritable
};
